import { useRef, useState } from "react";
import { uploadMedia } from "../lib/media";
import { Camera, Mic, Square, X, Loader2, FileAudio, Upload } from "lucide-react";
import { toast } from "sonner";

export default function MediaUpload({ onPhoto, onAudio }) {
  const [photoPreview, setPhotoPreview] = useState(null);
  const [photoLoading, setPhotoLoading] = useState(false);
  const [audioUrl, setAudioUrl] = useState(null);
  const [transcript, setTranscript] = useState(null);
  const [audioLoading, setAudioLoading] = useState(false);
  const [recording, setRecording] = useState(false);
  const photoInput = useRef();
  const audioInput = useRef();
  const recorderRef = useRef();
  const chunksRef = useRef([]);

  const handlePhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setPhotoLoading(true);
    try {
      const data = await uploadMedia(file, "photo");
      setPhotoPreview(URL.createObjectURL(file));
      onPhoto(data.path);
      toast.success("Foto caricata");
    } catch {
      toast.error("Caricamento foto fallito");
    } finally {
      setPhotoLoading(false);
    }
  };

  const clearPhoto = () => {
    setPhotoPreview(null);
    onPhoto(null);
    if (photoInput.current) photoInput.current.value = "";
  };

  const uploadAudioBlob = async (file) => {
    setAudioLoading(true);
    try {
      const data = await uploadMedia(file, "audio");
      setAudioUrl(URL.createObjectURL(file));
      setTranscript(data.transcript || "");
      onAudio(data.path, data.transcript || "");
      toast.success("Audio caricato e trascritto");
    } catch {
      toast.error("Caricamento audio fallito");
    } finally {
      setAudioLoading(false);
    }
  };

  const handleAudioFile = (e) => {
    const file = e.target.files?.[0];
    if (file) uploadAudioBlob(file);
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const rec = new MediaRecorder(stream);
      chunksRef.current = [];
      rec.ondataavailable = (ev) => ev.data.size > 0 && chunksRef.current.push(ev.data);
      rec.onstop = () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunksRef.current, { type: "audio/webm" });
        const file = new File([blob], "registrazione.webm", { type: "audio/webm" });
        uploadAudioBlob(file);
      };
      rec.start();
      recorderRef.current = rec;
      setRecording(true);
    } catch {
      toast.error("Microfono non disponibile");
    }
  };

  const stopRecording = () => {
    recorderRef.current?.stop();
    setRecording(false);
  };

  const clearAudio = () => {
    setAudioUrl(null);
    setTranscript(null);
    onAudio(null, null);
    if (audioInput.current) audioInput.current.value = "";
  };

  return (
    <div className="grid sm:grid-cols-2 gap-4">
      {/* PHOTO */}
      <div className="rounded-xl bg-[#111823] border border-white/8 p-4">
        <div className="flex items-center gap-2 text-sm font-semibold text-zinc-200 mb-3">
          <Camera className="w-4 h-4 text-[#06B6D4]" /> Foto del problema
        </div>
        {photoPreview ? (
          <div className="relative">
            <img src={photoPreview} alt="anteprima" className="w-full h-40 object-cover rounded-lg" data-testid="photo-preview" />
            <button onClick={clearPhoto} data-testid="clear-photo-btn" className="absolute top-2 right-2 p-1.5 rounded-lg bg-black/70 hover:bg-red-500/80 transition-colors">
              <X className="w-4 h-4" />
            </button>
          </div>
        ) : (
          <button
            onClick={() => photoInput.current?.click()}
            disabled={photoLoading}
            data-testid="upload-photo-btn"
            className="w-full h-40 rounded-lg border-2 border-dashed border-white/15 hover:border-[#F97316]/60 flex flex-col items-center justify-center gap-2 text-zinc-400 transition-colors"
          >
            {photoLoading ? <Loader2 className="w-6 h-6 animate-spin" /> : <Upload className="w-6 h-6" />}
            <span className="text-xs">Carica una foto (spia, perdita, componente)</span>
          </button>
        )}
        <input ref={photoInput} type="file" accept="image/*" onChange={handlePhoto} className="hidden" data-testid="photo-file-input" />
      </div>

      {/* AUDIO */}
      <div className="rounded-xl bg-[#111823] border border-white/8 p-4">
        <div className="flex items-center gap-2 text-sm font-semibold text-zinc-200 mb-3">
          <FileAudio className="w-4 h-4 text-[#06B6D4]" /> Ascolta il rumore
        </div>
        {audioLoading ? (
          <div className="h-40 flex flex-col items-center justify-center gap-2 text-zinc-400">
            <Loader2 className="w-6 h-6 animate-spin" />
            <span className="text-xs">Trascrizione in corso...</span>
          </div>
        ) : audioUrl ? (
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <audio controls src={audioUrl} className="w-full h-9" data-testid="audio-preview" />
              <button onClick={clearAudio} data-testid="clear-audio-btn" className="p-1.5 rounded-lg bg-black/50 hover:bg-red-500/80 transition-colors">
                <X className="w-4 h-4" />
              </button>
            </div>
            {transcript ? (
              <p className="text-xs text-zinc-400 italic bg-black/30 rounded p-2" data-testid="audio-transcript">"{transcript}"</p>
            ) : (
              <p className="text-xs text-zinc-500">Nessun parlato riconosciuto — il file audio verra comunque allegato.</p>
            )}
          </div>
        ) : (
          <div className="h-40 flex flex-col items-center justify-center gap-3">
            {recording ? (
              <button onClick={stopRecording} data-testid="stop-record-btn" className="flex items-center gap-2 px-4 py-2.5 rounded-lg bg-[#EF4444] text-white font-semibold animate-pulse">
                <Square className="w-4 h-4" /> Ferma registrazione
              </button>
            ) : (
              <button onClick={startRecording} data-testid="record-audio-btn" className="flex items-center gap-2 px-4 py-2.5 rounded-lg bg-[#F97316]/15 text-[#F97316] hover:bg-[#F97316]/25 font-semibold transition-colors">
                <Mic className="w-4 h-4" /> Registra il rumore
              </button>
            )}
            <button onClick={() => audioInput.current?.click()} data-testid="upload-audio-btn" className="text-xs text-zinc-400 hover:text-white underline">
              oppure carica un file audio
            </button>
          </div>
        )}
        <input ref={audioInput} type="file" accept="audio/*" onChange={handleAudioFile} className="hidden" data-testid="audio-file-input" />
      </div>
    </div>
  );
}
